/** Spikex Card 解锁所需 VIP 等级（与 eligibility.requiredLevel 对齐时的默认值） */
export const SPIKEX_CARD_UNLOCK_LEVEL = 6;

export type SpikexCardEntryStatus = {
  status?: string;
  balance?: string | null;
  cardId?: number | null;
  hyperPayCardId?: string | null;
  applyId?: number | null;
  rejectReason?: string | null;
};

export type SpikexCardApplyStatus = {
  applyStatus?: string;
  applyId?: number;
  mcTradeNo?: string;
  failReason?: string | null;
};

export type OverviewSpikexCardCtaTone = "primary" | "success" | "warning" | "danger" | "muted";

export type OverviewSpikexCardCta = {
  labelKey: string;
  labelValues?: Record<string, string | number>;
  tone: OverviewSpikexCardCtaTone;
  filled: boolean;
  showChevron: boolean;
};

function buildOverviewCta(
  partial: Omit<OverviewSpikexCardCta, "showChevron"> & { showChevron?: boolean }
): OverviewSpikexCardCta {
  return {
    ...partial,
    showChevron: partial.showChevron ?? !partial.filled,
  };
}

export function formatOverviewCardBalanceDisplay(value: string | number | null | undefined) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * 个人中心 Overview Spikex Card 入口右侧 CTA（对齐 ssr-web-index spikexCard 状态读取）
 */
export function resolveOverviewSpikexCardCta(
  entryStatus: SpikexCardEntryStatus | null,
  applyStatus: SpikexCardApplyStatus | null,
  options: { unlockLevel?: number } = {}
): OverviewSpikexCardCta {
  const status = entryStatus?.status;
  const unlockLevel = options.unlockLevel ?? SPIKEX_CARD_UNLOCK_LEVEL;

  if (!status || status === "NO_CARD") {
    return buildOverviewCta({ labelKey: "user.spikexCardApply", tone: "primary", filled: true });
  }

  if (status === "LEVEL_LOCKED") {
    return buildOverviewCta({
      labelKey: "user.spikexCardUnlock",
      labelValues: { level: unlockLevel },
      tone: "muted",
      filled: false,
    });
  }

  if (status === "REVIEWING") {
    const sub = applyStatus?.applyStatus;
    if (sub === "FREEZING") {
      return buildOverviewCta({ labelKey: "user.spikexCardProcessing", tone: "success", filled: false });
    }
    if (sub === "APPROVED") {
      return buildOverviewCta({ labelKey: "user.spikexCardIssuing", tone: "success", filled: false });
    }
    if (sub === "ACTIVATING") {
      return buildOverviewCta({ labelKey: "user.spikexCardActivating", tone: "success", filled: false });
    }
    return buildOverviewCta({ labelKey: "user.spikexCardReviewing", tone: "success", filled: false });
  }

  if (status === "WAIT_ATTACHMENT") {
    return buildOverviewCta({ labelKey: "user.spikexCardAttachment", tone: "warning", filled: false });
  }

  if (status === "REJECTED") {
    return buildOverviewCta({ labelKey: "user.spikexCardReapply", tone: "danger", filled: false });
  }

  if (status === "ACTIVE") {
    const balance = entryStatus?.balance;
    if (balance != null && balance !== "") {
      return buildOverviewCta({
        labelKey: "user.spikexCardBalance",
        labelValues: { balance: formatOverviewCardBalanceDisplay(balance) },
        tone: "success",
        filled: false,
      });
    }
    return buildOverviewCta({ labelKey: "user.spikexCardManage", tone: "success", filled: false });
  }

  if (status === "FROZEN") {
    return buildOverviewCta({ labelKey: "user.spikexCardFrozen", tone: "danger", filled: false });
  }

  return buildOverviewCta({ labelKey: "user.spikexCardApply", tone: "primary", filled: true });
}

/** entry=REVIEWING / WAIT_ATTACHMENT 时需拉取 apply/status 细化展示 */
export function shouldFetchSpikexCardApplyStatus(entryStatus: SpikexCardEntryStatus | null) {
  const status = entryStatus?.status;
  return status === "REVIEWING" || status === "WAIT_ATTACHMENT";
}
